import Mat3 from "ui/helpers/Mat3";
import ImageCenterResolver from "ui/canvas/helpers/ImageCenterResolver";
import {SCENE_SOURCE_HEIGHT, SCENE_SOURCE_WIDTH} from "ui/config";

export default class AbstractCanvasTransformTool {
	constructor() {
		/**
		 * @type {string}
		 * @private
		 */
		this._actionName = this.constructor.name;

		/**
		 * @type {CanvasSelection|null}
		 * @protected
		 */
		this._sel = null;

		/**
		 * @type {DataSet|null}
		 * @protected
		 */
		this._dataSet = null;

		/**
		 * @type {number}
		 * @protected
		 */
		this._sceneWidth = SCENE_SOURCE_WIDTH;

		/**
		 * @type {number}
		 * @protected
		 */
		this._sceneHeight = SCENE_SOURCE_HEIGHT;

		/**
		 * @type {?{x: number, y: number}}
		 * @protected
		 */
		this._center = null;

		/**
		 * @type {boolean}
		 * @protected
		 */
		this._needImageCenterResearch = true;
	}

	get actionName() {
		return this._actionName;
	}

	/**
	 * @param {CanvasSelection} sel
	 * @return {this}
	 */
	setCanvasSelection(sel) {
		this._sel = sel;
		return this;
	}

	/**
	 * @param {DataSet} dataSet
	 * @return {this}
	 */
	setDataSet(dataSet) {
		this._dataSet = dataSet;
		return this;
	}

	/**
	 * @param {number} w
	 * @param {number} h
	 * @return {this}
	 */
	setSceneSize(w, h) {
		this._sceneWidth = w;
		this._sceneHeight = h;
		return this;
	}

	/**
	 * Applies transformation to the layer
	 *
	 * @param {Canvas} canvas
	 * @param {CanvasLayer} layer
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {Promise<void>}
	 */
	async applyForLayer(canvas, layer, fromFrame = 0, toFrame = 0) {
		if (!this._dataSet) {
			throw new Error('DataSet was not defined');
		}

		if (fromFrame === -1) {
			fromFrame = layer.getFrame();
		}

		if (toFrame === -1) {
			toFrame = layer.getFrame();
		}

		if (fromFrame === toFrame) {
			if (layer.getFramesStatuses()[fromFrame] !== 1) {
				canvas.makeKeyFrame();
			}
		} else if (this._isNeedKeyframesForSelection(fromFrame, toFrame)) {
			canvas.makeKeyFrame();
		}

		await this._prepare(layer, fromFrame, toFrame);

		let c = document.createElement('canvas');
		c.width = this._sceneWidth;
		c.height = this._sceneHeight;
		let ctx = c.getContext('2d');

		if (this._needImageCenterResearch) {
			if (this._sel && this._sel.hasSelection && this._sel.isRectangularSelection) {
				const sel = this._sel.selectedAreaRect;
				this._center = {
					x: sel.x + sel.width / 2,
					y: sel.y + sel.height / 2,
				};
			} else {
				const centers = [];
				await layer.updateFrameImages(img => {
					centers.push(new ImageCenterResolver().getCenter(img));
				}, fromFrame, toFrame, true);

				this._center = {
					x: centers.reduce((acc, v) => acc + v.x, 0) / centers.length,
					y: centers.reduce((acc, v) => acc + v.y, 0) / centers.length,
				};
			}
		}

		if (layer.isReference) {
			this._processReference(layer, fromFrame, toFrame);
			return;
		}

		await layer.updateFrameImages(this._processImage.bind(this, c, ctx), fromFrame, toFrame);
		await this._clearMemory();
	}

	/**
	 * Returns TRUE if need to create key frames for selection
	 *
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @return {boolean}
	 * @private
	 */
	_isNeedKeyframesForSelection(fromFrame, toFrame) {
		// for overriding
		return false;
	}

	/**
	 * Returns true if need to apply anti-aliasing before drawing the image
	 *
	 * @returns {boolean}
	 */
	_isAntiAliasing() {
		return false;
	}

	/**
	 * @param {CanvasLayer} layer
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {void}
	 * @protected
	 */
	async _prepare(layer, fromFrame, toFrame) {
		// for overriding
	}

	/**
	 * @returns {void}
	 * @protected
	 */
	async _clearMemory() {
		// for overriding
	}

	/**
	 * @param {CanvasLayer} layer
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {void}
	 * @protected
	 */
	_processReference(layer, fromFrame, toFrame) {
		// for overriding
	}

	/**
	 * @param {HTMLCanvasElement} canvas
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {HTMLImageElement} img
	 * @param {number} frameIndex
	 * @returns {?T_DataSetItem}
	 * @protected
	 */
	_processImage(canvas, ctx, img, frameIndex) {
		if (!this._dataSet) {
			throw new Error('DataSet was not defined');
		}

		ctx.clearRect(0, 0, this._sceneWidth, this._sceneHeight);
		ctx.save();

		const mat = new Mat3(),
			sel = this._sel ? this._sel.selectedAreaRect : null;

		// clear rect in selection
		if (sel && this._sel && this._sel.hasSelection && this._sel.isRectangularSelection) {
			ctx.drawImage(img, 0, 0);
			ctx.globalCompositeOperation = 'destination-out';
			ctx.fillStyle = 'red';
			ctx.fillRect(sel.x, sel.y, sel.width, sel.height);
			ctx.globalCompositeOperation = 'source-over';
		}

		if (this._center) {
			mat.translate(-this._center.x, -this._center.y);
		}

		if (this._isAntiAliasing()) {
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = 'high';
		}

		this._transformImage(ctx, mat, frameIndex);

		if (this._center) {
			mat.translate(this._center.x, this._center.y);
		}

		let m = mat.values;

		ctx.setTransform(m[0], m[3], m[1], m[4], m[2], m[5]);

		if (sel && this._sel && this._sel.hasSelection && this._sel.isRectangularSelection) {
			ctx.drawImage(img, sel.x, sel.y, sel.width, sel.height, sel.x, sel.y, sel.width, sel.height);
		} else {
			ctx.drawImage(img, 0, 0);
		}

		ctx.restore();
		return this._dataSet.setData(canvas.toDataURL('image/png'));
	}

	/**
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {Mat3} mat
	 * @param {number} [frameIndex]
	 * @private
	 */
	_transformImage(ctx, mat, frameIndex) {
		ctx.translate(Math.round(this._shift.x), Math.round(this._shift.y));
	}
}
